import { useState, useEffect } from 'react';

export function useAnalytics(workspaceId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  const trackEvent = async (eventType, eventData) => {
    try {
      const response = await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          eventType,
          eventData
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to track event');
      }

      return await response.json();
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getDashboardData = async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        workspaceId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      const response = await fetch(`/api/analytics/dashboard?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const data = await response.json();
      setDashboardData(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getUserActivity = async (userId) => {
    try {
      const params = new URLSearchParams({ workspaceId, userId });
      const response = await fetch(`/api/analytics/user-activity?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user activity');
      }
      
      return await response.json();
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getWorkspaceSummary = async () => {
    try {
      const params = new URLSearchParams({ workspaceId });
      const response = await fetch(`/api/analytics/workspace-summary?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch workspace summary');
      }
      
      return await response.json();
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Optionally load some initial data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (workspaceId) {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      getDashboardData(oneWeekAgo, new Date()).catch(() => {});
    }
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    trackEvent,
    getDashboardData,
    getUserActivity,
    getWorkspaceSummary,
    dashboardData,
    loading,
    error
  };
}